import React, { useEffect, useRef, useState } from "react";
import {
  MdInsights,
  MdCloud,
  MdConstruction,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";

const GROUPS = [
  {
    key: "intelligence",
    label: "Intelligence",
    Icon: MdInsights,
    activeColor: "#2563eb",
    activeBg: "#eff6ff",
    activeBorder: "#bfdbfe",
    activeText: "#1d4ed8",
    darkActiveBg: "rgba(37,99,235,0.18)",
    darkActiveBorder: "#1d4ed8",
    darkActiveText: "#93c5fd",
  },
  {
    key: "conditions",
    label: "Conditions",
    Icon: MdCloud,
    activeColor: "#0891b2",
    activeBg: "#ecfeff",
    activeBorder: "#a5f3fc",
    activeText: "#0e7490",
    darkActiveBg: "rgba(8,145,178,0.18)",
    darkActiveBorder: "#0e7490",
    darkActiveText: "#67e8f9",
  },
  {
    key: "infra",
    label: "Infra. State",
    Icon: MdConstruction,
    activeColor: "#7c3aed",
    activeBg: "#f5f3ff",
    activeBorder: "#ddd6fe",
    activeText: "#6d28d9",
    darkActiveBg: "rgba(124,58,237,0.18)",
    darkActiveBorder: "#4c1d95",
    darkActiveText: "#c4b5fd",
  },
];

const ToggleSwitch = ({ checked, onToggle, activeColor, ariaLabel }) => {
  const handleKeyDown = (e) => {
    if (e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        position: "relative",
        width: 30,
        height: 16,
        borderRadius: 8,
        background: checked ? activeColor : "#9ca3af",
        border: "none",
        cursor: "pointer",
        padding: 0,
        transition: "background 0.2s",
        flexShrink: 0,
        outline: "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 14 : 2,
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.18s",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          display: "block",
        }}
      />
    </button>
  );
};

const LayerToggle = ({ groups, onToggle, isDark }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  const animate = (nextCollapsed) => {
    setVisible(false);
    timerRef.current = setTimeout(() => {
      setCollapsed(nextCollapsed);
      setVisible(true);
    }, 140);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const bg = isDark ? "rgba(17,24,39,0.95)" : "rgba(255,255,255,0.97)";
  const border = isDark ? "#374151" : "#e5e7eb";
  const titleColor = isDark ? "#f9fafb" : "#111827";
  const mutedColor = isDark ? "#6b7280" : "#9ca3af";
  const collapseButtonStyle = {
    width: 22,
    height: 22,
    borderRadius: 5,
    border: `1px solid ${border}`,
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: mutedColor,
    fontSize: 14,
    padding: 0,
  };

  const animationWrapper = {
    opacity: visible ? 1 : 0,
    transform: visible ? "scale(1)" : "scale(0.96)",
    transition: "opacity 0.14s ease, transform 0.14s ease",
  };

  if (collapsed) {
    return (
      <div
        role="group"
        aria-label="Map layer toggles"
        style={{
          ...animationWrapper,
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 10,
          padding: "8px 6px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
        }}
      >
        {GROUPS.map(({ key, label, Icon, activeColor, darkActiveText }) => {
          const isOn = groups[key] ?? false;
          const iconColor = isOn
            ? isDark
              ? darkActiveText
              : activeColor
            : mutedColor;

          return (
            <button
              key={key}
              role="switch"
              aria-checked={isOn}
              aria-label={`${label} layer ${isOn ? "on" : "off"}`}
              title={label}
              onClick={() => onToggle(key)}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                  onToggle(key);
                }
              }}
              tabIndex={0}
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                border: `1px solid ${
                  isOn ? (isDark ? darkActiveText : activeColor) : border
                }`,
                background: isOn
                  ? isDark
                    ? `rgba(255,255,255,0.07)`
                    : `${activeColor}14`
                  : isDark
                  ? "#1f2937"
                  : "#f9fafb",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: iconColor,
                fontSize: 16,
                transition: "all 0.15s",
                padding: 0,
              }}
            >
              <Icon />
            </button>
          );
        })}

        <button
          onClick={() => animate(false)}
          aria-label="Expand layer panel"
          title="Expand"
          style={{
            ...collapseButtonStyle,
            width: 30,
            height: 24,
            marginTop: 2,
          }}
        >
          <MdChevronRight />
        </button>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label="Map layer toggles"
      style={{
        ...animationWrapper,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 10,
        padding: "10px 12px",
        minWidth: 172,
        boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: titleColor,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Layers
        </span>
        <button
          onClick={() => animate(true)}
          aria-label="Collapse layer panel"
          title="Collapse"
          style={collapseButtonStyle}
        >
          <MdChevronLeft />
        </button>
      </div>

      {GROUPS.map(
        ({
          key,
          label,
          Icon,
          activeColor,
          activeBg,
          activeBorder,
          activeText,
          darkActiveBg,
          darkActiveBorder,
          darkActiveText,
        }) => {
          const isOn = groups[key] ?? false;
          const rowBg = isOn
            ? isDark
              ? darkActiveBg
              : activeBg
            : isDark
            ? "#1f2937"
            : "#f9fafb";
          const rowBorder = isOn
            ? isDark
              ? darkActiveBorder
              : activeBorder
            : border;
          const iconColor = isOn
            ? isDark
              ? darkActiveText
              : activeColor
            : mutedColor;
          const textColor = isOn
            ? isDark
              ? darkActiveText
              : activeText
            : isDark
            ? "#9ca3af"
            : "#6b7280";
          const toggleActiveColor = isDark ? darkActiveText : activeColor;

          return (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                borderRadius: 6,
                border: `1px solid ${rowBorder}`,
                background: rowBg,
                marginBottom: 6,
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              <Icon
                aria-hidden="true"
                style={{ fontSize: 15, color: iconColor, flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: textColor,
                  flex: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
              <ToggleSwitch
                checked={isOn}
                onToggle={() => onToggle(key)}
                activeColor={toggleActiveColor}
                ariaLabel={`Toggle ${label} layer`}
              />
            </div>
          );
        }
      )}
    </div>
  );
};

export default LayerToggle;
